import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setAuthToken, hasAnyAuth } from '@/lib/api';
import { ws } from '@/lib/ws';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Phase 16 — cookie-bridge bootstrap.
    // We try /auth/me whenever ANY auth is available: a legacy
    // localStorage token OR the cookie session (detected via the
    // companion CSRF cookie). When neither is present, skip the
    // network round-trip and bail straight to logged-out state.
    if (!hasAnyAuth()) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      ws.shouldReconnect = true;
      ws.connect();
      return data;
    } catch (e) {
      // Either the localStorage token is stale or the cookie expired — wipe
      // the legacy token and let the user log in fresh. The cookie itself
      // would already have expired server-side; nothing to clean there.
      setAuthToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setAuthToken(data.token);
    setUser(data.user);
    ws.shouldReconnect = true;
    ws.connect();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    setAuthToken(data.token);
    setUser(data.user);
    ws.shouldReconnect = true;
    ws.connect();
    return data.user;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); }
    catch (err) { console.warn('[auth] logout request failed (clearing client state anyway):', err?.response?.status || err?.message); }
    setAuthToken(null);
    setUser(null);
    ws.disconnect();
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    logout,
    refresh,
    // Convenience flag — many components destructure this directly.
    isAuthenticated: !!user,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
